// ─────────────────────────────────────────────────────────────
// supabase.ts — AnımsamA Platform
// animsama.com.tr · Tüm Supabase bağlantı ve veri işlemleri
// ─────────────────────────────────────────────────────────────

import { createClient, SupabaseClient } from '@supabase/supabase-js';

// ── Ortam değişkenleri (Vite veya CRA) ──────────────────────
const SUPABASE_URL: string =
  (import.meta as any).env?.VITE_SUPABASE_URL ||
  process.env.REACT_APP_SUPABASE_URL ||
  '';

const SUPABASE_ANON_KEY: string =
  (import.meta as any).env?.VITE_SUPABASE_ANON_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  '';

// ── Supabase istemcisi ───────────────────────────────────────
export const supabase: SupabaseClient | null =
  SUPABASE_URL && SUPABASE_ANON_KEY
    ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      })
    : null;

// ── Bağlantı kontrolü ────────────────────────────────────────
export const isSupabaseReady = (): boolean => supabase !== null;

// ─────────────────────────────────────────────────────────────
// YARDIMCI: hata işleme
// ─────────────────────────────────────────────────────────────
function handleError(context: string, error: any): never {
  console.error(`[supabase:${context}]`, error?.message || error);
  throw new Error(error?.message || `${context} başarısız`);
}

// ─────────────────────────────────────────────────────────────
// TERAPİST / DANIŞAN LİSTESİ
// ─────────────────────────────────────────────────────────────

/** Terapiste ait tüm danışanları getirir */
export async function fetchClients(therapistId?: string): Promise<any[]> {
  if (!supabase) return [];
  const query = supabase
    .from('clients')
    .select('*')
    .order('created_at', { ascending: false });

  if (therapistId) query.eq('therapist_id', therapistId);

  const { data, error } = await query;
  if (error) handleError('fetchClients', error);
  return data || [];
}

/** Danışan koduna göre danışanı getirir (Danışan girişinde kullanılır) */
export async function fetchClientByCode(code: string): Promise<any | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('clients')
    .select('*')
    .eq('code', code)
    .single();
  if (error) handleError('fetchClientByCode', error);
  return data;
}

// ─────────────────────────────────────────────────────────────
// ANILАР
// ─────────────────────────────────────────────────────────────

/** Danışana ait anıları getirir */
export async function fetchMemories(clientId: number | string): Promise<any[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('memories')
    .select('*')
    .eq('client_id', clientId)
    .order('year', { ascending: true });
  if (error) handleError('fetchMemories', error);
  return data || [];
}

/** Yeni anı ekler */
export async function addMemory(memory: {
  client_id: number | string;
  age_range?: string;
  title: string;
  valence?: number;
  tags?: string[];
  year?: string;
  notes?: string;
}): Promise<any> {
  if (!supabase) throw new Error('Supabase bağlantısı yok');
  const { data, error } = await supabase
    .from('memories')
    .insert([memory])
    .select()
    .single();
  if (error) handleError('addMemory', error);
  return data;
}

/** Anıyı siler */
export async function deleteMemory(id: number | string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('memories').delete().eq('id', id);
  if (error) handleError('deleteMemory', error);
}

// ─────────────────────────────────────────────────────────────
// GÜNLÜK
// ─────────────────────────────────────────────────────────────

/** Danışanın günlük kayıtlarını getirir */
export async function fetchDiary(clientId: number | string): Promise<any[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('diary_entries')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) handleError('fetchDiary', error);
  return data || [];
}

/** Yeni günlük kaydı ekler */
export async function addDiaryEntry(entry: {
  client_id: number | string;
  text: string;
  mood?: number;
}): Promise<any> {
  if (!supabase) throw new Error('Supabase bağlantısı yok');
  const { data, error } = await supabase
    .from('diary_entries')
    .insert([entry])
    .select()
    .single();
  if (error) handleError('addDiaryEntry', error);
  return data;
}

// ─────────────────────────────────────────────────────────────
// AİLE KATKILARI
// ─────────────────────────────────────────────────────────────

/** Danışana ait aile katkılarını getirir */
export async function fetchContributions(clientId: number | string): Promise<any[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('contributions')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) handleError('fetchContributions', error);
  return data || [];
}

/** Yeni aile katkısı ekler */
export async function addContribution(contribution: {
  client_id: number | string;
  from_name: string;
  relation?: string;
  type: 'audio' | 'photo' | 'text';
  content?: string;
  file_url?: string;
}): Promise<any> {
  if (!supabase) throw new Error('Supabase bağlantısı yok');
  const { data, error } = await supabase
    .from('contributions')
    .insert([{ ...contribution, is_read: false }])
    .select()
    .single();
  if (error) handleError('addContribution', error);
  return data;
}

/** Katkıyı okundu olarak işaretler */
export async function markContributionRead(id: number | string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from('contributions')
    .update({ is_read: true })
    .eq('id', id);
  if (error) handleError('markContributionRead', error);
}

// ─────────────────────────────────────────────────────────────
// DOSYA YÜKLEME
// ─────────────────────────────────────────────────────────────

/**
 * Storage bucket'a dosya yükler ve public URL döner.
 * Bucket adı: animsama-files (Supabase Storage'da oluşturulmalı)
 */
export async function uploadFile(
  file: File,
  folder: string = 'uploads'
): Promise<string> {
  if (!supabase) throw new Error('Supabase bağlantısı yok');
  const ext = file.name.split('.').pop();
  const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { error } = await supabase.storage
    .from('animsama-files')
    .upload(path, file, { upsert: false });

  if (error) handleError('uploadFile', error);

  const { data } = supabase.storage.from('animsama-files').getPublicUrl(path);
  return data.publicUrl;
}

// ─────────────────────────────────────────────────────────────
// SEANS NOTU
// ─────────────────────────────────────────────────────────────

/** Seans notu kaydeder */
export async function saveSessionNote(note: {
  client_id: number | string;
  therapist_id: string;
  content: string;
  session_number?: number;
  protocol?: string;
}): Promise<any> {
  if (!supabase) throw new Error('Supabase bağlantısı yok');
  const { data, error } = await supabase
    .from('session_notes')
    .insert([note])
    .select()
    .single();
  if (error) handleError('saveSessionNote', error);
  return data;
}
